import { JSONStringifyRich } from '@/utils/object'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Abi, Address, ContractFunctionName, isAddressEqual } from 'viem'
import { estimateContractGas } from 'viem/actions'
import { UseSimulateContractParameters, useChainId, usePublicClient } from 'wagmi'
import { useOriginChainId } from './useOriginChainId'
import { useAccount } from '@/domain/hooks/useAccount'

const GAS_LIMIT_BUFFER = 100_000n
// Percentage buffer for gas limit (20% = 120n / 100n)
const GAS_LIMIT_PERCENT_MULTIPLIER = 120n
const GAS_LIMIT_PERCENT_DIVISOR = 100n

const GAS_LIMIT_MIN = 1_500_000n

const TRANSACTIONS_WITH_MIN_GAS_LIMIT: CreateConfigEntryResult[] = []

export interface UseIncreasedGasLimitResult {
  data: bigint | undefined
  isLoading: boolean
  isReady: boolean
}

export function useIncreasedGasLimit(params: UseSimulateContractParameters<Abi, string>): UseIncreasedGasLimitResult {
  const chainId = useChainId()
  const account = useAccount()
  const originChainId = useOriginChainId()
  const client = usePublicClient()

  const { address, abi, functionName, args } = params

  // Check if this transaction needs a minimum gas floor (e.g., flash loans)
  const shouldUseMinGas =
    !!address &&
    TRANSACTIONS_WITH_MIN_GAS_LIMIT.some(
      (config) =>
        config.chainId === originChainId &&
        config.address &&
        isAddressEqual(config.address, address) &&
        config.functionName === functionName,
    )

  // Always buffer gas for all transactions - gas limit is just a ceiling, users only pay for actual usage
  const shouldRunQuery = !!account && !!address

  const { data, isLoading, isError } = useQuery({
    queryKey: ['estimate-gas', chainId, address, account, functionName, JSONStringifyRich(args)],
    queryFn:
      client && address && abi && functionName && shouldRunQuery
        ? async () => {
            const estimateWithBuffer = async () => {
              const gas = await estimateContractGas(client, {
                account,
                address,
                abi,
                functionName,
                args,
              })

              // Use percentage-based buffer (20%) with minimum floor
              const gasWithPercent = (gas * GAS_LIMIT_PERCENT_MULTIPLIER) / GAS_LIMIT_PERCENT_DIVISOR
              const gasWithFloor = gas + GAS_LIMIT_BUFFER
              return gasWithPercent > gasWithFloor ? gasWithPercent : gasWithFloor
            }

            if (shouldUseMinGas) {
              try {
                const gasWithBuffer = await estimateWithBuffer()
                return gasWithBuffer > GAS_LIMIT_MIN ? gasWithBuffer : GAS_LIMIT_MIN
              } catch (error) {
                console.warn('Gas estimation failed, using minimum fallback gas limit', error)
                return GAS_LIMIT_MIN
              }
            }

            return estimateWithBuffer()
          }
        : skipToken,
    retry: 0, //do not retry as it's just an approximation that helps transactions fail less frequently
    gcTime: 0,
  })

  return {
    data,
    isLoading,
    isReady: !shouldRunQuery || !!data || isError, // if error, ignore trying to increase gas limit
  }
}

interface CreateConfigEntryParams<
  TAbi extends Abi,
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
> {
  chainId: number
  address: Address
  abi: TAbi
  functionName: TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'> ? TFunctionName : never
}
interface CreateConfigEntryResult {
  address: Address
  chainId: number
  functionName: string
}

// needed for type safety
export function createConfigEntry<
  TAbi extends Abi,
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
>({ chainId, functionName, address }: CreateConfigEntryParams<TAbi, TFunctionName>): CreateConfigEntryResult {
  return {
    address,
    chainId,
    functionName,
  }
}
